import { IconListDetails } from '@tabler/icons-react';
import { Fragment, use } from 'react';
import { z } from 'zod';

import { KNOWLEDGE_GRAPH_GROUP } from '#core/lib/expr/providers/groups.ts';
import type { DatasetProvider } from '#core/lib/expr/providers/types.ts';
import { useEvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';

import { ToolStatusRow } from '#plugin-chat/components/tool-display/ToolStatusRow.tsx';
import { ViewExternalIdBadgeList } from '#plugin-chat/components/tool-display/ViewExternalIdBadgeList.tsx';
import { listDescribableViewNames } from '#plugin-chat/components/tools/describe-views/describable-views.ts';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { ColumnSchemaTable, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { ViewNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * Build the `views` field schema. Element type is parameterized so the
 * permissive base schema (used for runtime type inference) and the dynamic
 * enum (used for the LLM-facing JSON Schema) share one source of truth for
 * the field's `.min(1)` constraint and its `.describe()` text.
 */
const viewsField = (element: z.ZodType<string>) =>
  element.array().min(1).describe('View externalIds to describe. Must be drawn from the available views enum.');

/**
 * Permissive shape for {@link DescribeViewsInput} type inference. Validates
 * the runtime payload structure (`{ views: string[] }`) without constraining
 * which specific externalIds are accepted — the dynamic enum constraint is
 * applied per render in {@link DescribeViewsTool} from the live view catalogue.
 */
const DescribeViewsInputBaseSchema = z.object({
  views: viewsField(z.string()),
});

/** @public */
export type DescribeViewsInput = z.infer<typeof DescribeViewsInputBaseSchema>;

/** @public */
export type DescribeViewsOutput = {
  success: true;
};

export function DescribeViewsTool({
  filterProvider,
  ...props
}: ToolProps & { filterProvider?: (provider: readonly [string, DatasetProvider | undefined]) => boolean }) {
  const { viewNames, getProvider } = useEvalDeps();
  const tools = use(ToolsContext);

  // The view enum and the queryKnowledgeGraph describe-first guard share one
  // source of truth — `listDescribableViewNames`: every knowledge-graph name
  // whose provider can describe itself. A name whose provider has no `describe`
  // stays queryable but is omitted here rather than offered then un-renderable.
  const describable = listDescribableViewNames({ viewNames, getProvider }, filterProvider);

  // Empty catalogue is a routing/config bug — `z.enum` requires a non-empty
  // tuple. Surface as a null mount rather than crash.
  if (describable.length === 0) {
    return null;
  }
  const viewIds = describable as [string, ...string[]];
  const parametersSchema = DescribeViewsInputBaseSchema.extend({ views: viewsField(z.enum(viewIds)) });

  return (
    <Tool<DescribeViewsInput, DescribeViewsOutput>
      collapsible
      color="grape"
      description={
        <ToolGuidance
          description="Returns view description and a markdown table of columns with type and per-column description for each view."
          whenToUse={[
            `Before calling ${tools.QueryKnowledgeGraph} to discover which properties a view exposes and their types`,
            'When you need to pick the right property names for filters or projections',
          ]}
        >
          <ul>
            {describable?.map((externalId) => (
              <li key={externalId}>
                {externalId}: {getProvider?.(KNOWLEDGE_GRAPH_GROUP, externalId)?.describe?.(externalId)?.description}
              </li>
            ))}
          </ul>
        </ToolGuidance>
      }
      execute={() => ({
        success: true,
        flowSignal: {
          forceTool: tools.QueryKnowledgeGraph,
          appendMessages: [
            {
              role: 'user',
              content: 'Proceed to perform your planned queries *sequentially*.',
            },
          ],
        },
      })}
      icon={IconListDetails}
      labelValues={({ args }) => ({ count: args.views?.length ?? 0 })}
      message={{
        call: () => null,
        result: ({ args }) =>
          [...new Set(args.views)].map((externalId) => {
            const doc = getProvider?.(KNOWLEDGE_GRAPH_GROUP, externalId)?.describe?.(externalId);
            if (!doc) {
              throw new ViewNotFoundError(externalId, viewIds);
            }
            return (
              <Fragment key={externalId}>
                <h2>{externalId} data model view schema</h2>
                {!!doc.description && <p>{doc.description}</p>}
                <ColumnSchemaTable columns={[...doc.columns]} />
              </Fragment>
            );
          }),
      }}
      parameters={parametersSchema}
      render={{
        running: ({ args }) => <ToolStatusRow extras={<ViewExternalIdBadgeList externalIds={args.views ?? []} />} />,
        complete: ({ args }) => <ToolStatusRow extras={<ViewExternalIdBadgeList externalIds={args.views ?? []} />} />,
      }}
      {...props}
    />
  );
}
